import ExcelJS from 'exceljs'
import FileSaver from 'file-saver'
import { TFunction } from 'i18next'
import { isoDateStringToLocalFormat } from 'ComponentLibrary/src/common/date'
import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'
import { AreaValue, getLeadingValuePerSqm, getLeadingValuePerSqmLabels, MonetaryValue, Percentage } from 'ComponentLibrary'
import { YearPeriod } from 'ComponentLibrary/src/common/year-period'
import { formatExcelCell, ValueType } from 'ComponentLibrary/src/common/excel'

const STARTING_COLUMN_INDEX = 1
const STARTING_ROW_INDEX = 1
const COLUMN_WIDTH = 27

type ValuationWithAddress = {
    valuation: ExistingValuation
    addressText: string
}

type ExportTranslationProps = {
    tTerminologyCommon: TFunction
    tTerminologyExport: TFunction
    locale: string
}

type TransformValuationToWorksheet = {
    workbook: ExcelJS.Workbook
    valuationWithAddress: ValuationWithAddress & ExportTranslationProps
}

export async function exportAllValuesToExcel(valuationWithAddress: ValuationWithAddress & ExportTranslationProps) {
    const { valuation, addressText, tTerminologyExport, locale } = valuationWithAddress
    const formattedValuationDate = isoDateStringToLocalFormat(valuation?.valuation_date, false, locale)

    const workbook = new ExcelJS.Workbook()
    const allValuesTitle = `${addressText}-${formattedValuationDate}`.replace(/[\s.\\/]+/g, '-')

    transformAllValuesToWorksheet({ workbook, valuationWithAddress })

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

    FileSaver.saveAs(blob, `${tTerminologyExport('allValues.fileNamePrefix')}-${allValuesTitle}.xlsx`)
}

function processAllValues<T extends ValueType>(
    worksheet: ExcelJS.Worksheet,
    rowIndex: number,
    rowLabel: string,
    valuationValue: T | undefined,
    locale: string
) {
    if (valuationValue !== undefined) {
        const row = worksheet.getRow(rowIndex)
        const labelCell = row.getCell(STARTING_COLUMN_INDEX)
        labelCell.value = rowLabel
        labelCell.font = { bold: true, size: 14 }

        const valueCell = formatExcelCell(valuationValue, undefined, undefined, locale)

        const cells = [valueCell]
        cells.forEach((cellData, index) => {
            const cell = row.getCell(index + STARTING_COLUMN_INDEX + 1)
            cell.value = cellData.value
            cell.alignment = { horizontal: 'right' }
            cell.font = { size: 14 }
            if (cellData.numFmt) {
                cell.numFmt = cellData.numFmt
            }
        })
    }
}

function transformAllValuesToWorksheet({ workbook, valuationWithAddress }: TransformValuationToWorksheet) {
    const { tTerminologyExport, locale } = valuationWithAddress
    const worksheet = workbook.addWorksheet(tTerminologyExport('allValues.worksheetName'))
    let currentRow = STARTING_ROW_INDEX

    const columnOne = worksheet.getColumn(1)
    columnOne.width = COLUMN_WIDTH
    const columnTwo = worksheet.getColumn(2)
    columnTwo.width = COLUMN_WIDTH

    const valuesToExport = getValuesToExport(valuationWithAddress)
    valuesToExport.forEach(({ label, value }) => {
        processAllValues(worksheet, currentRow, label, value, locale)
        currentRow++
    })

    return worksheet
}

const getValuesToExport = ({
    valuation,
    addressText,
    tTerminologyCommon,
    tTerminologyExport,
    locale,
}: ValuationWithAddress & ExportTranslationProps): Array<{ label: string; value: ValueType | undefined }> => {
    if (!valuation) {
        return []
    }

    const allStructures = valuation.economic_units.flatMap((economicUnit) => economicUnit.structures)
    const { valuePerSqmLabel, valuePerSqmValue } = getLeadingValuePerSqm(valuation, getLeadingValuePerSqmLabels(tTerminologyCommon))

    return [
        { label: tTerminologyCommon('asset'), value: addressText },
        {
            label: tTerminologyExport('allValues.rows.valuationDate'),
            value: isoDateStringToLocalFormat(valuation.valuation_date, false, locale),
        },
        {
            label: tTerminologyCommon('marketValue'),
            value: valuation.market_value && MonetaryValue.fromMonetaryValueDataObject(valuation.market_value),
        },
        {
            label: tTerminologyCommon('valuationMethods.land_value'),
            value: valuation.selected_valuation_methods.includes('land_value')
                ? MonetaryValue.fromMonetaryValueDataObject(valuation.market_value_by_valuation_method.land_value)
                : undefined,
        },
        {
            label: tTerminologyCommon('valuationMethods.income'),
            value: valuation.selected_valuation_methods.includes('income')
                ? MonetaryValue.fromMonetaryValueDataObject(valuation.market_value_by_valuation_method.income)
                : undefined,
        },
        {
            label: tTerminologyCommon('valuationMethods.investment_method'),
            value: valuation.selected_valuation_methods.includes('investment_method')
                ? MonetaryValue.fromMonetaryValueDataObject(valuation.investment_method_market_value)
                : undefined,
        },
        {
            label: tTerminologyCommon('valuationMethods.cost'),
            value: valuation?.selected_valuation_methods.includes('cost')
                ? MonetaryValue.fromMonetaryValueDataObject(valuation?.market_value_by_valuation_method.cost)
                : undefined,
        },
        {
            label: tTerminologyCommon('valuationMethods.comparison'),
            value: valuation.selected_valuation_methods.includes('comparison')
                ? MonetaryValue.fromMonetaryValueDataObject(valuation.market_value_by_valuation_method.comparison)
                : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.discountedCashflowShort'),
            value: valuation.selected_valuation_methods.includes('discounted_cashflow')
                ? MonetaryValue.fromMonetaryValueDataObject(valuation.market_value_by_valuation_method.discounted_cashflow)
                : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.transactionCosts'),
            value: valuation.transaction_costs ? MonetaryValue.fromMonetaryValueDataObject(valuation.transaction_costs) : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.marketValueIncludingTransactionCosts'),
            value: valuation.transaction_costs
                ? MonetaryValue.fromMonetaryValueDataObject(valuation.market_value_including_transaction_costs)
                : undefined,
        },
        {
            label: valuePerSqmLabel,
            value: valuePerSqmValue ? MonetaryValue.fromMoneyPerAreaValue(valuePerSqmValue) : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.mortgageLendingValue'),
            value: valuation.mortgage_lending_value && MonetaryValue.fromMonetaryValueDataObject(valuation.mortgage_lending_value),
        },
        {
            label: tTerminologyExport('allValues.rows.mortgageLendingValueWithMethod', {
                method: tTerminologyCommon('valuationMethods.land_value'),
            }),
            value: valuation.selected_mortgage_lending_valuation_methods.includes('land_value')
                ? MonetaryValue.fromMonetaryValueDataObject(valuation.mortgage_lending_value_by_valuation_method.land_value)
                : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.mortgageLendingValueWithMethod', {
                method: tTerminologyCommon('valuationMethods.income'),
            }),
            value: valuation.selected_mortgage_lending_valuation_methods.includes('income')
                ? MonetaryValue.fromMonetaryValueDataObject(valuation.mortgage_lending_value_by_valuation_method.income)
                : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.mortgageLendingValueWithMethod', {
                method: tTerminologyCommon('valuationMethods.cost'),
            }),
            value: valuation.selected_mortgage_lending_valuation_methods.includes('cost')
                ? MonetaryValue.fromMonetaryValueDataObject(valuation.mortgage_lending_value_by_valuation_method.cost)
                : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.mortgageLendingValueWithMethod', {
                method: tTerminologyCommon('valuationMethods.comparison'),
            }),
            value: valuation.selected_mortgage_lending_valuation_methods.includes('comparison')
                ? MonetaryValue.fromMonetaryValueDataObject(valuation.mortgage_lending_value_by_valuation_method.comparison)
                : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.grossRentalIncome'),
            value:
                valuation.tenant_summary.total?.gross_rental_income.total &&
                MonetaryValue.fromMonetaryValueDataObject(valuation.tenant_summary.total.gross_rental_income.total),
        },
        {
            label: tTerminologyCommon('contractRent'),
            value:
                valuation.tenant_summary.total?.actual_rental_income.total &&
                MonetaryValue.fromMonetaryValueDataObject(valuation.tenant_summary.total.actual_rental_income.total),
        },
        {
            label: tTerminologyCommon('marketRent'),
            value:
                valuation.tenant_summary.total?.gross_rental_income.total &&
                MonetaryValue.fromMonetaryValueDataObject(valuation.tenant_summary.total.gross_rental_income.total),
        },
        {
            label: tTerminologyExport('allValues.rows.managementCosts'),
            value:
                valuation.tenant_summary.total?.management_cost.total &&
                MonetaryValue.fromMonetaryValueDataObject(valuation.tenant_summary.total.management_cost.total),
        },
        {
            label: tTerminologyExport('allValues.rows.netRentalIncome'),
            value:
                valuation.tenant_summary.total?.net_rental_income.total &&
                MonetaryValue.fromMonetaryValueDataObject(valuation.tenant_summary.total.net_rental_income.total),
        },
        {
            label: tTerminologyCommon('wault'),
            value: valuation.wault
                ? valuation.wault === 'unlimited'
                    ? tTerminologyExport('allValues.values.waultUnlimited')
                    : valuation.wault
                : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.grossYieldMultiplier'),
            value: valuation.selected_valuation_methods.includes('income') ? valuation.gross_yield_multiplier : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.netYieldMultiplier'),
            value: valuation.selected_valuation_methods.includes('income') ? valuation.net_yield_multiplier : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.grossYieldVerbose'),
            value:
                valuation.selected_valuation_methods.includes('income') && valuation.gross_yield !== undefined
                    ? new Percentage(valuation.gross_yield)
                    : undefined,
        },
        {
            label: tTerminologyCommon('grossInitialYield'),
            value:
                valuation.selected_valuation_methods.includes('income') && valuation.gross_initial_yield !== undefined
                    ? new Percentage(valuation.gross_initial_yield)
                    : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.discountRate'),
            value:
                valuation.selected_valuation_methods.includes('income') && valuation.discount_rate
                    ? new Percentage(valuation.discount_rate)
                    : undefined,
        },
        {
            label: tTerminologyCommon('rentableArea'),
            value: valuation.rentable_area?.value ? new AreaValue(valuation.rentable_area?.value) : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.grossFloorArea'),
            value: valuation.gross_floor_area?.value ? new AreaValue(valuation.gross_floor_area?.value) : undefined,
        },
        {
            label: tTerminologyExport('allValues.rows.landArea'),
            value: valuation.land_area?.value ? new AreaValue(valuation.land_area?.value) : undefined,
        },
        ...allStructures.map((structure) => ({
            label: tTerminologyExport('allValues.rows.residualUsefulLifeWithStructure', { structureTitle: structure.title }),
            value: new YearPeriod(structure.life_span.custom_residual ?? structure.life_span.calculated_residual),
        })),
        {
            label: tTerminologyExport('allValues.rows.reportLink'),
            value: `${window.location.origin}/assets/${valuation.asset_uuid}/valuations/${valuation.uuid}/overview`,
        },
    ].filter((item) => item.value !== undefined)
}
